import React from 'react';
import CreditCard from 'lucide-react/dist/esm/icons/credit-card.js';
import Lock from 'lucide-react/dist/esm/icons/lock.js';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2.js';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle.js';
import { usePurchasePage } from './PurchasePageContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslations } from '../../../contexts/TranslationContext';

const ERROR_COPY = {
  'Purchase plans are still loading. Please wait a moment and try again.': {
    key: 'purchase.errors.plansLoading',
    fallback: 'Purchase plans are still loading. Please wait a moment and try again.',
  },
  'Failed to initialize checkout — no payment provider returned': {
    key: 'purchase.errors.checkoutProviderMissing',
    fallback: 'Failed to initialize checkout - no payment provider returned',
  },
  'Checkout failed. Please try again.': {
    key: 'purchase.errors.checkoutFailed',
    fallback: 'Checkout failed. Please try again.',
  },
};

export default function PurchasePaymentSection({ section, config }) {
  const {
    step,
    locationConfirmed,
    setLocationConfirmed,
    termsAccepted,
    setTermsAccepted,
    paymentProcessing,
    paymentSuccess,
    paymentError,
    cartTotal,
    cartItemCount,
    proceedToCheckout,
    isLoggedIn,
    isGuestCheckoutEnabled,
    isAnonymousGuestCheckoutEnabled,
    guestInfo,
    setGuestInfo,
    guestErrors,
  } = usePurchasePage();
  const { c } = useContent();
  const { t: tr } = useTranslations();

  if (step !== 2) {
    // Only show when on payment step
    return null;
  }

  const total = cartTotal();
  const itemCount = cartItemCount();
  const editableError = (message) => {
    const copy = ERROR_COPY[message];
    if (!copy) return message;

    return (
      <InlineTranslationEdit
        translationKey={copy.key}
        value={c(copy.key, tr(copy.key) || copy.fallback)}
        defaultText={copy.fallback}
      />
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 md:p-8 shadow-lg mb-8">
      <div className="flex items-center gap-3 mb-6">
        <CreditCard className="w-6 h-6 text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            <InlineTranslationEdit
              translationKey="purchase.payment.title"
              value={c('purchase.payment.title', tr('purchase.payment.title') || 'Payment & Checkout')}
              defaultText="Payment & Checkout"
            />
        </h2>
      </div>

      {paymentSuccess ? (
        <div className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            <InlineTranslationEdit
              translationKey="purchase.payment.successTitle"
              value={c('purchase.payment.successTitle', tr('purchase.payment.successTitle') || 'Payment Successful!')}
              defaultText="Payment Successful!"
            />
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            <InlineTranslationEdit
              translationKey="purchase.payment.successDesc"
              value={c('purchase.payment.successDesc', tr('purchase.payment.successDesc') || 'Your order has been confirmed. Check your email for details.')}
              defaultText="Your order has been confirmed. Check your email for details."
            />
          </p>
        </div>
      ) : (
        <>
          {/* Order Summary */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              <InlineTranslationEdit
                translationKey="purchase.payment.orderSummary"
                value={c('purchase.payment.orderSummary', tr('purchase.payment.orderSummary') || 'Order Summary')}
                defaultText="Order Summary"
              />
            </h3>
            <div className="space-y-2 text-gray-700 dark:text-gray-300">
              <div className="flex justify-between">
                <span>
                  <InlineTranslationEdit
                    translationKey="purchase.payment.totalItems"
                    value={c('purchase.payment.totalItems', tr('purchase.payment.totalItems') || 'Total Items:')}
                    defaultText="Total Items:"
                  />
                </span>
                <span className="font-semibold">{itemCount}</span>
              </div>
              <div className="flex justify-between text-xl font-bold text-blue-600 pt-2 border-t border-gray-200 dark:border-gray-700">
                <span>
                  <InlineTranslationEdit
                    translationKey="purchase.payment.total"
                    value={c('purchase.payment.total', tr('purchase.payment.total') || 'Total:')}
                    defaultText="Total:"
                  />
                </span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Guest Info Form — shown when standard guest checkout is enabled without anonymous guest checkout */}
          {!isLoggedIn && isGuestCheckoutEnabled && isGuestCheckoutEnabled() && !isAnonymousGuestCheckoutEnabled?.() && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{tr('purchase.payment.guestTitle') || 'Your Details'}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {tr('purchase.payment.guestSubtitle') || 'Enter your information to continue as a guest.'}
              </p>
              {[
                { key: 'name', label: tr('purchase.payment.fullName') || 'Full Name', type: 'text', placeholder: 'Jane Smith' },
                { key: 'email', label: tr('purchase.payment.emailAddress') || 'Email Address', type: 'email', placeholder: 'jane@example.com' },
                { key: 'phone', label: tr('purchase.payment.phone') || 'Phone (optional)', type: 'tel', placeholder: '+1 555 000 0000' },
              ].map(({ key, label, type, placeholder }) => (
                <div key={key} className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
                  <input
                    type={type}
                    value={guestInfo[key]}
                    placeholder={placeholder}
                    onChange={(e) => {
                      setGuestInfo(prev => ({ ...prev, [key]: e.target.value }));
                      if (guestErrors[key]) setGuestErrors(prev => ({ ...prev, [key]: undefined }));
                    }}
                    className={`w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 ${guestErrors[key] ? 'border-red-400 ring-1 ring-red-400' : 'border-gray-300 dark:border-gray-600'}`}
                  />
                  {guestErrors[key] && <p className="text-xs text-red-500 mt-1">{guestErrors[key]}</p>}
                </div>
              ))}
              <div className="mt-3 pt-3 border-t border-blue-200/60 dark:border-blue-800/60">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!guestInfo.ageConfirmed}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, ageConfirmed: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                    {tr('login.ageGate.checkbox', 'I confirm that I am the age of majority.')}
                  </span>
                </label>
                {guestErrors.age && <p className="text-xs text-red-500 mt-1">{guestErrors.age}</p>}
              </div>
            </div>
          )}

          {/* Terms & Conditions */}
          <div className="space-y-4 mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={locationConfirmed}
                onChange={(e) => setLocationConfirmed(e.target.checked)}
                className="mt-1 w-5 h-5 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                <InlineTranslationEdit
                  translationKey="purchase.payment.location_check"
                  value={c('purchase.payment.location_check', tr('purchase.payment.location_check') || 'I confirm I am physically located in an eligible jurisdiction')}
                  defaultText="I confirm I am physically located in an eligible jurisdiction"
                />
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                <InlineTranslationEdit
                  translationKey="purchase.payment.terms_check"
                  value={c('purchase.payment.terms_check', tr('purchase.payment.terms_check') || 'I accept the Terms & Conditions and Privacy Policy')}
                  defaultText="I accept the Terms & Conditions and Privacy Policy"
                />
              </span>
            </label>
          </div>

          {/* Error message */}
          {paymentError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-red-700 dark:text-red-300 text-sm">{editableError(paymentError)}</p>
            </div>
          )}

          {/* Payment button */}
          <button
            onClick={proceedToCheckout}
            disabled={!locationConfirmed || !termsAccepted || paymentProcessing || itemCount === 0}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg"
          >
            {paymentProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <InlineTranslationEdit
                  translationKey="purchase.payment.processing"
                  value={c('purchase.payment.processing', tr('purchase.payment.processing') || 'Processing...')}
                  defaultText="Processing..."
                />
              </>
            ) : (
              <>
                <Lock className="w-5 h-5" />
                <InlineTranslationEdit
                  translationKey="purchase.payment.completePurchase"
                  value={c('purchase.payment.completePurchase', tr('purchase.payment.completePurchase') || 'Complete Purchase')}
                  defaultText="Complete Purchase"
                /> ${total.toFixed(2)}
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-4">
            <Lock className="w-3 h-3 inline mr-1" />
            <InlineTranslationEdit
              translationKey="purchase.payment.securePayment"
              value={c('purchase.payment.securePayment', tr('purchase.payment.securePayment') || 'Secure payment powered by industry-leading encryption')}
              defaultText="Secure payment powered by industry-leading encryption"
            />
          </p>
        </>
      )}
    </div>
  );
}
